import React from 'react';

import { Button, Pagination, Select, Table, Toolbar } from '../../../design-system/components/desktop';
import { ConsoleEmpty, ConsoleError, StatusPill } from '../../../design-system/patterns/console';
import { OrderItemsCell, parseRand, shortOrderId } from '../../Dashboard/dashboard.parts';
import { formatCurrency } from '../../../utils/common';
import { routes } from '../../../routes/routes';

import '../../Dashboard/dashboard.css';

/**
 * The buyer's order history, the view. Pure.
 *
 * A buyer's own orders are something they OWN, not something they are browsing,
 * so this is a table and not a grid of cards. Section 7.
 *
 * "Bought in person" is passed through as the shipment status exactly as
 * before. It is not one of the values `utils/orderStatus.js` knows, so it falls
 * through to the untinted pill, which is the honest outcome: an unrecognised
 * status gets no colour opinion rather than a guessed one.
 */
export function OrderHistoryTableView({
  rows = [],
  loading = false,
  error,
  onRetry,

  search = '',
  onSearchChange,
  status = '',
  onStatusChange,
  statusOptions = [],

  page = 1,
  pageCount = 1,
  pageSize = 10,
  total = 0,
  onPageChange,

  onView,
  drawer = null,
}) {
  const filtered = Boolean(search || status);

  const columns = [
    { key: 'order_id', header: 'Order', render: (row) => shortOrderId(row.order_id) },
    {
      key: 'items',
      header: 'Cards',
      render: (row) => <OrderItemsCell items={row.items} />,
    },
    { key: 'seller', header: 'Seller', render: (row) => row.seller || 'Not recorded' },
    {
      key: 'total',
      header: 'Paid',
      numeric: true,
      render: (row) => formatCurrency(parseRand(row.total)),
    },
    {
      key: 'order_status',
      header: 'Payment',
      render: (row) => <StatusPill status={row.order_status} />,
    },
    {
      key: 'shipment_status',
      header: 'Shipment',
      render: (row) =>
        row.bought_in_person ? (
          'Bought in person'
        ) : row.shipment_status ? (
          <StatusPill status={row.shipment_status} />
        ) : (
          'Not booked yet'
        ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        data={error ? [] : rows}
        rowKey={(row, index) => row.order_id || index}
        loading={loading}
        onRowClick={(row) => onView && onView(row)}
        emptyState={
          error ? (
            <ConsoleError error={error} onRetry={onRetry} title="Could not load your orders" />
          ) : (
            <ConsoleEmpty
              filtered={filtered}
              title="You have not ordered anything yet"
              text="Every order you place shows up here, with where it has got to."
              filteredText="No order matches that search or status. Clear one of them and your orders come back."
              action={
                <Button as="a" href={routes.MARKETPLACE} variant="ghost" size="sm">
                  Browse the marketplace
                </Button>
              }
              onClear={() => {
                if (onSearchChange) onSearchChange('');
                if (onStatusChange) onStatusChange('');
              }}
            />
          )
        }
        rowActions={(row) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              onView(row);
            }}
          >
            View order
          </Button>
        )}
        toolbar={
          <Toolbar
            search={search}
            onSearchChange={onSearchChange}
            searchPlaceholder="Filter by order or card"
            searchLabel="Filter your orders"
            count={loading ? undefined : `${total} ${total === 1 ? 'order' : 'orders'}`}
            filters={
              <div className="dsa-dash__filters">
                <Select
                  options={statusOptions}
                  value={status}
                  onChange={onStatusChange}
                  placeholder="All statuses"
                  label="Filter by order status"
                />
              </div>
            }
          />
        }
        pagination={
          pageCount > 1 ? (
            <Pagination
              page={page}
              pageCount={pageCount}
              pageSize={pageSize}
              totalItems={total}
              itemNoun="orders"
              onPageChange={onPageChange}
            />
          ) : null
        }
      />
      {drawer}
    </>
  );
}

export default OrderHistoryTableView;
